/**
 * Renders a sortable table of patients with their basic information and procedures of interest.
 * Provides functionality to add new patients and navigate to individual patient detail views.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ScrollView,
  Pressable,
  ActivityIndicator,
} from "react-native";

import global from "../../../../styles/globalStyles";

import { capitalizeString } from "../../../../utils/functions";
import { ModalOpener } from "../../../uncommon/native/modals";
import {
  TPatientInfo,
  TPatientsPagination,
} from "../../../../services/api-clinic";
import {
  NormalText,
  SearchInput,
  SortHeader,
  TitleText,
  SecondaryButton,
} from "../../../common/native";
import { TClinicPatientType } from "../../../../utils/types";
import { PrimaryButton } from "../../../common/native";
import { SessionContext } from "../../../../contexts";

type PatientListProps = {
  storePatientDetail: (currentPatientDetail: TPatientInfo) => void;
  patients: TPatientInfo[];
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  addNewPatient: (newPatientInfo: TPatientInfo) => void;
  isErrorGettingPatients: boolean;
  hasFetchedPatients: boolean;
  patientType?: TClinicPatientType;
  emptyState?: {
    title: string;
    description: string;
    ctaLabel?: string;
    useAddModal?: boolean;
    onCtaPress?: () => void;
  };
  pagination?: TPatientsPagination | null;
  canGoNext?: boolean;
  canGoPrevious?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  isPaginationLoading?: boolean;
  unlockedFreeLeadUuid?: string | null;
};

const PatientList: React.FC<PatientListProps> = ({
  storePatientDetail,
  patients,
  searchTerm,
  onSearchTermChange,
  addNewPatient,
  isErrorGettingPatients,
  hasFetchedPatients,
  patientType = TClinicPatientType.PATIENT,
  emptyState,
  pagination = null,
  canGoNext = false,
  canGoPrevious = false,
  onNextPage,
  onPreviousPage,
  isPaginationLoading = false,
  unlockedFreeLeadUuid = null,
}) => {
  const navigate = useNavigate();
  const session = React.useContext(SessionContext)?.session;
  const isFreePlan = session?.planType === "FREE";
  const [sortedPatientListElements, setSortedPatientListElements] = useState<
    TPatientInfo[]
  >([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    order: "asc" | "desc";
  }>({
    field: "updatedAt",
    order: "desc",
  });

  const filteredPatients = useMemo(() => {
    return patients;
  }, [patients]);

  const sortPatientListElements = (
    field: string,
    order: "asc" | "desc",
    sourcePatients: TPatientInfo[] = filteredPatients,
    shouldUpdateSortConfig = true,
  ) => {
    if (shouldUpdateSortConfig) {
      setSortConfig((prev) =>
        prev.field === field && prev.order === order ? prev : { field, order },
      );
    }
    const sortingFunction = (a: TPatientInfo, b: TPatientInfo) => {
      const valueA = a[field as keyof TPatientInfo];
      const valueB = b[field as keyof TPatientInfo];

      if (
        field === "dateOfBirth" ||
        field === "createdAt" ||
        field === "updatedAt"
      ) {
        // @ts-ignore
        const dateA = valueA ? new Date(valueA) : null;
        // @ts-ignore
        const dateB = valueB ? new Date(valueB) : null;

        if (dateA && dateB) {
          return order === "asc"
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        } else if (dateA) {
          return order === "asc" ? -1 : 1;
        } else if (dateB) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      } else {
        const stringValueA = valueA ? valueA.toString().toLowerCase() : "";
        const stringValueB = valueB ? valueB.toString().toLowerCase() : "";

        if (stringValueA < stringValueB) return order === "asc" ? -1 : 1;
        if (stringValueA > stringValueB) return order === "asc" ? 1 : -1;
        return 0;
      }
    };

    const sorted = [...sourcePatients].sort(sortingFunction);
    setSortedPatientListElements(sorted);
  };

  useEffect(() => {
    sortPatientListElements(
      sortConfig.field,
      sortConfig.order,
      filteredPatients,
      false,
    );
  }, [filteredPatients, sortConfig.field, sortConfig.order]);

  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);

  const renderPatientRow = ({
    item,
    index,
  }: {
    item: TPatientInfo;
    index: number;
  }) => {
    const isHovered = index === hoveredIndex;
    const isUnlockedFreeLead =
      item.type === TClinicPatientType.LEAD &&
      item.uuid === unlockedFreeLeadUuid;
    const shouldMaskLeadContactFields =
      isFreePlan &&
      item.type === TClinicPatientType.LEAD &&
      !isUnlockedFreeLead;

    return (
      <Pressable
        onPress={() => storePatientDetail(item)}
        onHoverIn={() => setHoveredIndex(index)}
        onHoverOut={() => setHoveredIndex(-1)}
        style={[
          styles.patientListRow,
          item.type === TClinicPatientType.LEAD && styles.patientListRowLead,
          isHovered && styles.patientListRowHovered,
        ]}
      >
        <Text style={global.text} numberOfLines={1}>
          {item.updatedAt
            ? `${new Date(item.updatedAt).toLocaleDateString()}`
            : "-"}
        </Text>
        <Text style={global.text} numberOfLines={1}>
          {`${item.firstName || ""} ${item.lastName || "-"}`}
        </Text>
        <Text style={global.text} numberOfLines={1}>
          {item.type ? capitalizeString(item.type) : "-"}
        </Text>
        <Text style={global.text} numberOfLines={1}>
          {item.proceduresOfInterest
            ? item.proceduresOfInterest
                .map((item) => capitalizeString(item))
                .join(", ")
            : "-"}
        </Text>
        <View style={styles.contactCell}>
          {shouldMaskLeadContactFields ? (
            <PrimaryButton
              text="Upgrade to unlock"
              onPress={() => navigate("/upgrade")}
              style={styles.upgradeCellButton}
            />
          ) : (
            <Text style={global.text} numberOfLines={1}>
              {item.email || "-"}
            </Text>
          )}
        </View>
        <View style={styles.contactCell}>
          {shouldMaskLeadContactFields ? (
            <PrimaryButton
              text="Upgrade to unlock"
              onPress={() => navigate("/upgrade")}
              style={styles.upgradeCellButton}
            />
          ) : (
            <Text style={global.text} numberOfLines={1}>
              {item.phone || "-"}
            </Text>
          )}
        </View>
        <Text style={global.text} numberOfLines={1}>
          {item.location || "-"}
        </Text>
        <Text style={global.text} numberOfLines={1}>
          {item.uploadType || "-"}
        </Text>
      </Pressable>
    );
  };

  const currentPageNumber =
    pagination && pagination.limit > 0
      ? Math.floor(pagination.offset / pagination.limit) + 1
      : 1;

  return (
    <>
      <ModalOpener
        visible={visible}
        modalType={"AddANewPatient"}
        onClose={() => setVisible(false)}
        addNewPatient={addNewPatient}
        patientType={patientType}
      />
      <View style={styles.searchContainer}>
        <SearchInput
          value={searchTerm}
          onChangeText={onSearchTermChange}
          onClear={() => onSearchTermChange("")}
          placeholder="Search patients by name, email, or phone..."
          containerStyle={styles.searchInputContainer}
        />
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={true}>
        <View style={{ flexDirection: "column" }}>
          <View style={styles.patientListHeader}>
            <SortHeader
              label="Date Updated"
              field="updatedAt"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
            <SortHeader
              label="Name"
              field="firstName"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
            <SortHeader
              label="Type"
              field="type"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
            <SortHeader
              label="Procedures"
              field="proceduresOfInterest"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
            <SortHeader
              label="Email"
              field="email"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
            <SortHeader
              label="Phone"
              field="phone"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
            <SortHeader
              label="Location"
              field="location"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
            <SortHeader
              label="Source"
              field="uploadType"
              sortConfig={sortConfig}
              onSortChange={sortPatientListElements}
            />
          </View>
          {!hasFetchedPatients ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#00B7F4" />
            </View>
          ) : (
            <>
              {patients.length === 0 && !isErrorGettingPatients && (
                <View style={styles.emptyLeadsContainer}>
                  <TitleText
                    text={emptyState?.title || "No patients yet!"}
                    style={{ marginBottom: 10 }}
                  />
                  <NormalText
                    text={
                      emptyState?.description ||
                      "Add a new patient to start managing your cases."
                    }
                    style={{ marginBottom: 20, maxWidth: 400 }}
                  />
                  {emptyState?.ctaLabel && (
                    <PrimaryButton
                      text={emptyState.ctaLabel}
                      onPress={() => {
                        if (emptyState.useAddModal) {
                          setVisible(true);
                          return;
                        }
                        emptyState.onCtaPress?.();
                      }}
                    />
                  )}
                </View>
              )}
              {patients.length > 0 &&
                sortedPatientListElements.length === 0 &&
                !isErrorGettingPatients && (
                  <View style={styles.emptyLeadsContainer}>
                    <TitleText
                      text="No matching patients found"
                      style={{ marginBottom: 10 }}
                    />
                    <NormalText
                      text={`No results for "${searchTerm}". Try a different search.`}
                      style={{ marginBottom: 20, maxWidth: 400 }}
                    />
                  </View>
                )}

              {isErrorGettingPatients ? (
                <NormalText
                  text={
                    "Something wrong with getting patients. Please try again later"
                  }
                  style={{ marginTop: 20 }}
                />
              ) : (
                <FlatList
                  showsVerticalScrollIndicator={false}
                  data={sortedPatientListElements}
                  keyExtractor={(item, index) => item.uuid || `${index}`}
                  renderItem={renderPatientRow}
                />
              )}
              {pagination && !isErrorGettingPatients && (
                <View style={styles.paginationContainer}>
                  <View style={styles.paginationButtons}>
                    <SecondaryButton
                      text="Previous"
                      onPress={() => onPreviousPage?.()}
                      disabled={isPaginationLoading || !canGoPrevious}
                      style={styles.paginationButton}
                    />
                    <SecondaryButton
                      text="Next"
                      onPress={() => onNextPage?.()}
                      disabled={isPaginationLoading || !canGoNext}
                      style={styles.paginationButton}
                    />
                  </View>
                  <NormalText
                    text={`Page ${currentPageNumber}`}
                    style={styles.paginationLabel}
                  />
                  <SecondaryButton
                    text="Add Patient"
                    onPress={() => setVisible(true)}
                    style={[styles.paginationButton, styles.addPatientButton]}
                  />
                </View>
              )}
            </>
          )}
        </View>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  searchContainer: {
    marginBottom: 12,
  },
  searchInputContainer: {
    maxWidth: 560,
  },
  patientListHeader: {
    // @ts-ignore comment
    display: "grid",
    gridTemplateColumns: "120px 200px 120px 170px 300px 170px 250px 150px",
    borderBottomColor: "#DDDEE0",
    borderBottomWidth: 1,
    paddingVertical: 20,
    position: "relative",
    zIndex: 1,
  },
  patientListRow: {
    // @ts-ignore comment
    display: "grid",
    gridTemplateColumns: "120px 200px 120px 170px 300px 170px 250px 150px",
    paddingVertical: 2,
    marginVertical: 2,
    position: "relative",
    zIndex: 1,
  },
  patientListRowHovered: {
    backgroundColor: "#f0f0f0",
  },
  patientListRowLead: {
    backgroundColor: "#EEF6EE",
  },
  emptyLeadsContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
    borderRadius: 12,
    backgroundColor: "#FAFAFA",
    borderWidth: 1,
    borderColor: "#EEE",
    marginTop: 20,
    marginBottom: 20,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  paginationContainer: {
    marginTop: 16,
    marginBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  paginationLabel: {
    color: "#444A57",
    marginLeft: 12,
  },
  paginationButtons: {
    flexDirection: "row",
    gap: 8,
  },
  paginationButton: {
    height: 40,
    paddingHorizontal: 16,
  },
  addPatientButton: {
    marginLeft: 12,
  },
  upgradeCellButton: {
    height: "auto",
    minHeight: 34,
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignSelf: "flex-start",
    width: "auto",
    maxWidth: "100%",
  },
  contactCell: {
    justifyContent: "center",
    alignItems: "flex-start",
  },
});

export default PatientList;
